import React, { PropsWithChildren, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus } from '@fortawesome/free-solid-svg-icons';
import { Inventory, NftMintIdRes } from 'src/interfaces';
import { Button } from 'src/common/interactions/Button';
import { Select } from 'src/common/components/forms/Select';
import { classNames, formatCurrencyWithoutDenomination } from 'src/common/util';
import ToolTip from 'src/common/components/exchange/ToolTip/ToolTip';
import { ShardsContext } from 'src/common/context/ShardsContext';
import { PaymentOptions } from '../payment-options/PaymentOptions';

interface PurchaseOptionTableProps {
  itemQuantity: any;
  nftItem: NftMintIdRes;
  onAddCartButtonClick: (inv: Inventory, quantitySelected: string) => void;
  setItemQuantity: (value: React.SetStateAction<Record<string, unknown>>) => void;
}

const TableHeader: React.FC<PropsWithChildren<{ header: string }>> = ({ header, children }) => {
  return (
    <th
      scope="col"
      className="sticky top-0 border-b z-10 border-gray-300 bg-gray-50 bg-opacity-75 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter px-3 py-3.5"
      key={header}
    >
      {children || header}
    </th>
  );
};

type TableCellProps = { className: string; isLastRow: boolean; cellKey: string };
const TableCell: React.FC<PropsWithChildren<TableCellProps>> = ({ children, className, cellKey, isLastRow }) => {
  return (
    <td
      className={classNames(
        !isLastRow ? '' : 'border-b border-gray-200',
        className,
        'py-4 px-3 text-sm font-medium text-gray-900 ',
      )}
      key={cellKey}
    >
      {children}
    </td>
  );
};

export const PurchaseOptionTable = ({
  itemQuantity,
  nftItem,
  onAddCartButtonClick,
  setItemQuantity,
}: PurchaseOptionTableProps) => {
  const bundleOrSingletonTypes = ['bundle', 'litpet'];
  const isNFTBundleOrSingleton = bundleOrSingletonTypes.some((item) => nftItem.nftType.toLowerCase().includes(item));
  const { isShardDown } = useContext(ShardsContext);
  const disabledByShardDown = isShardDown(nftItem.shardUrl);

  return (
    <div className="my-8 flex flex-col h-full">
      <div className="flex-grow overflow-y-auto shadow-sm ring-1 ring-black ring-opacity-5 h-full rounded m-1">
        <table className="table-auto w-full border-separate overflow-y-scroll" style={{ borderSpacing: 0 }}>
          <thead className="bg-gray-50 ">
            <tr>
              {!isNFTBundleOrSingleton && (
                <>
                  <TableHeader header="Mint Rank" />

                  <TableHeader header="Price" />
                </>
              )}
              <TableHeader header="Seller" />

              <TableHeader header="Currency">
                <div className="flex items-center">
                  Payment Options
                  <ToolTip title="payment" className="ml-1 text-blue-400" />
                </div>
              </TableHeader>

              <TableHeader header="Add to Cart">
                <span className="sr-only">Add to Cart</span>
              </TableHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white overflow-y-auto h-full">
            {nftItem.inventory.map((item, i) => {
              const quantitySelected = `quantitySelected-${item.salePrice}-${item.sellerId}`;
              const isLastRow = i === nftItem.inventory.length - 1;
              const rowKey = `row-index-${i}-rank-${item.mintRank || 1}`;
              const getCellKey = (cellName: string) => `${rowKey}-cell-${cellName}`;
              return (
                <tr key={rowKey}>
                  {!isNFTBundleOrSingleton && (
                    <>
                      <TableCell {...{ className: 'whitespace-nowrap', cellKey: getCellKey('mint-rank'), isLastRow }}>
                        # {item.mintRank || 1} of {nftItem.mintTotal}
                      </TableCell>
                      <TableCell {...{ className: 'whitespace-nowrap', cellKey: getCellKey('price'), isLastRow }}>
                        ${formatCurrencyWithoutDenomination(item.salePrice)}
                      </TableCell>
                    </>
                  )}

                  <TableCell {...{ className: 'flex-wrap', cellKey: getCellKey('seller'), isLastRow }}>
                    <a
                      className="no-underline hover:underline"
                      href={`/search?seller=${item.sellerId}&searchOnlySellerItems=true`}
                    >
                      {item.sellerId}
                    </a>
                  </TableCell>
                  <TableCell {...{ className: 'flex-wrap', cellKey: getCellKey('currency'), isLastRow }}>
                    <PaymentOptions paymentOptions={item.paymentOptions} />
                  </TableCell>
                  <TableCell
                    {...{
                      className: 'whitespace-nowrap h-full flex justify-end',
                      cellKey: getCellKey('add-to-cart'),
                      isLastRow,
                    }}
                  >
                    <Button.Primary
                      key={`${item.mintRank}-button-checkout`}
                      type="button"
                      className="w-max gtm_addToCart col-start-1"
                      data-gtm="addToCart"
                      onClick={() => onAddCartButtonClick(item, `quantitySelected-${item.salePrice}-${item.sellerId}`)}
                      disabled={item.isLocked || item?.isBanned || disabledByShardDown}
                    >
                      Add to Cart
                      <FontAwesomeIcon className="ml-2" icon={faCartPlus} />
                    </Button.Primary>
                    {isNFTBundleOrSingleton && (
                      <Select
                        className={`border inline-block w-24 ml-2 justify-self-center mr-4 ${
                          Number(item.quantity) === 1 ? 'hidden' : ''
                        }`}
                        value={itemQuantity[quantitySelected] || 1}
                        onChange={(event) => {
                          const newItemQuantity = { ...itemQuantity };
                          newItemQuantity[quantitySelected] = event.target.value;
                          setItemQuantity(newItemQuantity);
                        }}
                      >
                        {[...Array(Number(item.quantity)).keys()].map((n) => (
                          <option value={n + 1} key={`${n}-${item.quantity}-quantity`}>
                            {n + 1}
                          </option>
                        ))}
                      </Select>
                    )}
                  </TableCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
